import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Filter from './Filter';
import ListWithPagination from './ListWithPagination';
import { Navbar, Container, Nav, Button, Modal } from 'react-bootstrap';
import { Filter as FilterIcon, List as ListIcon, Gear } from 'react-bootstrap-icons';
import '../styles/Dashboard.css';

type Filters = {
  codigo_suscripcion: string;
  payment_method_code: string;
  payment_option: string;
  subscr_status: string;
  account_nif: string;
  consultant_nif: string;
  invoice_account_nif: string;
  legal_name_account: string;
  descripcion: string;
  recurrency: string;
  last_payment: string | null;
  next_payment: string | null;
  remuneration: string | null;
  item_number: string;
  item_description: string;
  pack_description: string;
  subscription_start_date: string;
};

const Dashboard: React.FC = () => {
  const [filters, setFilters] = useState<Filters>({
    codigo_suscripcion: '',
    payment_method_code: '',
    payment_option: '',
    subscr_status: '',
    account_nif: '',
    invoice_account_nif: '',
    consultant_nif: '',
    legal_name_account: '',
    descripcion: '',
    recurrency: '',
    last_payment: null,
    next_payment: null,
    remuneration: null,
    item_number: '',
    item_description: '',
    pack_description: '',
    subscription_start_date: ''
  });

  const [tempFilters, setTempFilters] = useState<Filters>({
    codigo_suscripcion: '',
    payment_method_code: '',
    payment_option: '',
    subscr_status: '',
    account_nif: '',
    // ELIMINAR esta línea duplicada: invoice_account_nif: '',
    consultant_nif: '',
    invoice_account_nif: '', // Mantener solo una vez
    legal_name_account: '',
    descripcion: '',
    recurrency: '',
    last_payment: null,
    next_payment: null,
    remuneration: null,
    item_number: '',
    item_description: '',
    pack_description: '',
    subscription_start_date: ''
  });

  const [showFilters, setShowFilters] = useState(true);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const navigate = useNavigate();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  // Check if any filter has a value
  const hasFilterValues = (filtersObj: Filters): boolean => {
    return Object.values(filtersObj).some(value => 
      value !== '' && value !== null && value !== undefined
    );
  };

  // Check if filters have changed from initial state
  const hasFiltersChanged = (): boolean => {
    return hasFilterValues(tempFilters);
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
    }
  }, [navigate]);

  const handleClearFilters = () => {
    setTempFilters({
      codigo_suscripcion: '',
      payment_method_code: '',
      payment_option: '',
      subscr_status: '',
      account_nif: '',
      consultant_nif: '',
      invoice_account_nif: '',
      legal_name_account: '',
      descripcion: '',
      recurrency: '',
      last_payment: null,
      next_payment: null,
      remuneration: null,
      item_number: '',
      item_description: '',
      pack_description: '',
      subscription_start_date: ''
    });
  };

  const handleApplyFilters = () => {
    setFilters(tempFilters);
    setHasAppliedFilters(true);
    setShowFilters(false);
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('authToken');
    const oauthLogoutUrl = process.env.REACT_APP_API_BASE_URL + '/auth/logout';
    const postLogoutRedirectUri = process.env.REACT_APP_REDIRECT_URI ? process.env.REACT_APP_REDIRECT_URI : "";

    if (token) {
      try {
        await fetch(`${apiBaseUrl}/auth/revoke`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        localStorage.removeItem('authToken');

        const logoutUrl = `${oauthLogoutUrl}?redirectUri=${encodeURIComponent(postLogoutRedirectUri)}`;
        window.location.href = logoutUrl;
      } catch (error) {
        console.error('Error cerrando la sesión:', error);
        localStorage.removeItem('authToken');
        window.location.href = postLogoutRedirectUri;
      }
    } else {
      console.warn('No hay token para cerrar sesión');
      window.location.href = postLogoutRedirectUri;
    }
  };

  return (
    <div className="dashboard-page">
      {/* Navbar */}
      <Navbar bg="danger" variant="dark" expand="lg" sticky="top" className="shadow">
        <Container fluid>
          <Navbar.Brand href="#">
            <img
              src="/Logos_rgb-07.png"
              alt="DEH Online"
              className="d-inline-block align-top"
              height="40"
            />
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="responsive-navbar-nav" />
          <Navbar.Collapse id="responsive-navbar-nav">
            <Nav className="me-auto">
              <Nav.Link href="#subscriptions">
                <ListIcon /> Suscripciones
              </Nav.Link>
              <Nav.Link href="#settings">
                <Gear /> Configuración
              </Nav.Link>
            </Nav>
            <Nav>
              <Nav.Link onClick={handleLogout}>Cerrar Sesión</Nav.Link>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Main Content */}
      <div className="custom-container mt-4">
        <div className="text-center mb-4">
          <h2 className="text-red-shadow">Listado de Suscripciones</h2>
        </div>

        {/* Filter Button */}
        <div className="d-flex justify-content-end mb-3">
          <Button
            variant="outline-danger"
            onClick={() => setShowFilters(true)}
            className="filter-button"
          >
            <FilterIcon /> Filtros
          </Button>
        </div>

        {/* Modal para filtros */}
        <Modal show={showFilters} onHide={() => {
          if (hasAppliedFilters || !hasFilterValues(tempFilters)) {
            setShowFilters(false);
            if (!hasAppliedFilters) {
              setFilters({
                codigo_suscripcion: '',
                payment_method_code: '',
                payment_option: '',
                subscr_status: '',
                account_nif: '',
                invoice_account_nif: '',
                consultant_nif: '',
                legal_name_account: '',
                descripcion: '',
                recurrency: '',
                last_payment: null,
                next_payment: null,
                remuneration: null,
                item_number: '',
                item_description: '',
                pack_description: '',
                subscription_start_date: ''
              });
            }
          }
        }}>
          <Modal.Header closeButton>
            <Modal.Title>Filtros</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Filter filters={tempFilters} setFilters={setTempFilters} />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClearFilters}>
              Limpiar
            </Button>
            <Button 
              variant="danger" 
              onClick={handleApplyFilters}
              disabled={!hasFiltersChanged()}
            >
              Aplicar
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ListWithPagination */}
        <div className="list-wrapper mt-4 p-3 bg-white rounded shadow-sm">
          <ListWithPagination 
            filters={filters} 
            initialLoad={!hasAppliedFilters}
          />
        </div>
      </div>

      <footer className="login-footer">
        <p>© {new Date().getFullYear()} DEH Online. Todos los derechos reservados.</p>
      </footer>
    </div>
  );
};

export default Dashboard;